import React from "react";
import { Box, Typography, FormControl, Select, MenuItem } from "@mui/material";
import {
  Category,
  Person,
  Flag,
  LocalOffer as TagIcon,
  CalendarToday,
} from "@mui/icons-material";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/hooks";
import { selectSelectedItem } from "@/features/bughurd/boardSelector";
import { useLocation } from "react-router-dom";
import { height } from "@mui/system";

interface GroupByOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface SubGroupOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface GroupBySelectorProps {
  groupBy: string;
  sortOrder: string;
  onGroupByChange: (value: string) => void;
  onSortOrderChange: (value: string) => void;
  viewType?: string;
  showSubGrouping?: boolean;
  subGroupBy?: string;
  onSubGroupByChange?: (value: string) => void;
}

const GroupBySelector: React.FC<GroupBySelectorProps> = ({
  groupBy,
  sortOrder,
  onGroupByChange,
  onSortOrderChange,
  viewType,
  showSubGrouping,
  subGroupBy,
  onSubGroupByChange,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const selectedItem = useAppSelector(selectSelectedItem);
  const { pathname } = useLocation();
  const isMyTaskView =
    selectedItem === "My Tasks" ||
    pathname.includes("/defect-tracker/my-tasks");

  const groupByOptions: GroupByOption[] = [
    ...(viewType === "table"
      ? [
          {
            value: "None",
            label: t("None"),
            icon: <RemoveCircleOutlineOutlinedIcon />,
          },
        ]
      : []),
    { value: "Status", label: t("Status"), icon: <Category /> },
    ...(!isMyTaskView
      ? [
          {
            value: "Assignee",
            label: t("Assignee"),
            icon: <Person />,
          },
        ]
      : []),
    { value: "Priority", label: t("Priority"), icon: <Flag /> },
    { value: "Tags", label: t("Tags"), icon: <TagIcon /> },
    { value: "Task type", label: t("Task type"), icon: <Category /> },
  ];

  // Get available sub-group options based on current group selection
  const getSubGroupOptions = (): SubGroupOption[] => {
    const allOptions = [
      {
        value: "None",
        label: t("None"),
        icon: <RemoveCircleOutlineOutlinedIcon />,
      },
      { value: "Status", label: t("Status"), icon: <Category /> },
      ...(!isMyTaskView
        ? [
            {
              value: "Assignee",
              label: t("Assignee"),
              icon: <Person />,
            },
          ]
        : []),
      { value: "Priority", label: t("Priority"), icon: <Flag /> },
      { value: "Tags", label: t("Tags"), icon: <TagIcon /> },
      {
        value: "Due date",
        label: t("Due date"),
        icon: <CalendarToday />,
      },
      {
        value: "Start date",
        label: t("Start date"),
        icon: <CalendarToday />,
      },
      { value: "Task type", label: t("Task type"), icon: <Category /> },
    ];

    return allOptions.filter((option) => option.value !== groupBy);
  };

  // Helper function to get the current group by option
  const getCurrentGroupByOption = () => {
    return groupByOptions.find((option) => option.value === groupBy);
  };

  // Helper function to get the current sub-group by option
  const getCurrentSubGroupByOption = () => {
    return getSubGroupOptions().find(
      (option) => option.value === (subGroupBy || "None")
    );
  };

  // Shared select styles
  const selectStyles = {
    bgcolor: "inherit",
    fontSize: "12px",
    height: 28,
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      gap: 1,
    },
  };

  // Shared label styles
  const labelStyles = {
    fontWeight: 500,
    fontSize: "12px",
    color: "text.secondary",
    whiteSpace: "nowrap",
  };

  // Shared value text styles
  const valueTextStyles = {
    fontSize: "12px",
    color: "text.primary",
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
      }}
    >
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          value={groupBy}
          onChange={(e) => onGroupByChange(e.target.value)}
          displayEmpty
          sx={selectStyles}
          renderValue={(value) => {
            const currentOption = getCurrentGroupByOption();
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={labelStyles}>{t("Group by:")}</Typography>
                {currentOption?.icon}
                <Typography sx={valueTextStyles}>
                  {currentOption?.label}
                </Typography>
              </Box>
            );
          }}
        >
          {groupByOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {option.icon}
                <Typography sx={{ fontSize: "12px" }}>
                  {option.label}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showSubGrouping && (
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={subGroupBy || "None"}
            onChange={(e) => onSubGroupByChange?.(e.target.value)}
            sx={selectStyles}
            renderValue={(value) => {
              const currentOption = getCurrentSubGroupByOption();
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={labelStyles}>{t("Sub-group by:")}</Typography>
                  {currentOption?.icon}
                  <Typography sx={valueTextStyles}>
                    {currentOption?.label}
                  </Typography>
                </Box>
              );
            }}
          >
            {getSubGroupOptions()?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {option.icon}
                  <Typography sx={{ fontSize: "12px" }}>
                    {option.label}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {/* Uncomment if you want to show sort options */}
      {/* <FormControl size="small" sx={{ minWidth: 140 }}>
        <Select
          value={sortOrder}
          onChange={(e) => onSortOrderChange(e.target.value)}
          sx={{ bgcolor: "white" }}
        >
          {sortOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Select>
      </FormControl> */}
      {/* Uncomment if you want to show reset button */}
      {/* <IconButton
        size="small"
        onClick={() => {
          onGroupByChange("Status");
          onSortOrderChange("Ascending");
        }}
        sx={{
          ml: 1,
          color: "text.secondary",
          "&:hover": { color: "error.main" },
        }}
      >
        <Delete />
      </IconButton> */}{" "}
    </Box>
  );
};

export default GroupBySelector;
